/**
 * Client-side image resize/compression helpers.
 *
 * Uses the browser canvas API — no external dependencies required. The output
 * is a JPEG (or PNG if the source has alpha) that respects maxWidth/maxHeight
 * while preserving aspect ratio.
 */

export interface ResizeOptions {
  /** Max output width in px. Default: 1024. */
  maxWidth?: number;
  /** Max output height in px. Default: 1024. */
  maxHeight?: number;
  /** JPEG quality 0..1. Default: 0.85. */
  quality?: number;
  /** Output mime type. Default: image/jpeg. */
  mimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
}

const DEFAULTS: Required<ResizeOptions> = {
  maxWidth: 1024,
  maxHeight: 1024,
  quality: 0.85,
  mimeType: 'image/jpeg',
};

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err instanceof Error ? err : new Error('Failed to load image'));
    };
    img.src = url;
  });
}

function fitWithin(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) return { width, height };
  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Resize an image file in the browser. Returns a new File ready to upload.
 * If the source is already within the max dimensions and is a JPEG, the
 * original file is returned unchanged.
 */
export async function resizeImageFile(file: File, options: ResizeOptions = {}): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  // SVG / GIF: skip resize to preserve animation/vector. Caller may still upload.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

  const opts: Required<ResizeOptions> = { ...DEFAULTS, ...options };

  const img = await loadImage(file);
  const { width: srcW, height: srcH } = img;
  const { width: outW, height: outH } = fitWithin(srcW, srcH, opts.maxWidth, opts.maxHeight);

  // Fast path: already small enough and the format matches — return original.
  if (outW === srcW && outH === srcH && file.type === opts.mimeType) {
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, outW, outH);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null'))),
      opts.mimeType,
      opts.quality,
    );
  });

  const baseName = file.name.replace(/\.[^.]+$/, '');
  const ext = opts.mimeType === 'image/png' ? 'png' : opts.mimeType === 'image/webp' ? 'webp' : 'jpg';
  return new File([blob], `${baseName}.${ext}`, { type: opts.mimeType, lastModified: Date.now() });
}
